import { lazy } from 'react';

// project-imports
import MainLayout from 'layout/MainLayout';
import CommonLayout from 'layout/CommonLayout';
import Loadable from 'components/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import PermissionGuard from 'utils/route-guard/PermissionGuard';
import ClientsPage from 'pages/application/clients';
import TeamsPage from 'pages/application/teams';
import VideoSessionPage from 'pages/application/video-session';

import QuickFlowApp from 'pages/application/quikflow';
import TransactionHistory from 'pages/application/dashboard/DashboardTransactions/TransactionHistory';
import TransactionDetails from 'pages/application/dashboard/DashboardTransactions/TransactionDetails';
import TransactionOrder from 'pages/transactions';
import UnmappedTransactionsData from 'pages/transactions/unmapped';
import NewDashboard from 'pages/application/NewDashboard';
import IndividualTransactionDetails from 'pages/transactions/details';
import Users from 'pages/users';
import Resources from 'pages/resources';
import Announcements from 'pages/announcements';
import MissedCalls from 'pages/application/dashboard/MissedCalls';
import DashboardAnalytics from 'pages/dashboard-analytics';
import AiSupport from 'pages/ai-support';
import Help from 'pages/help';
import { PERMISSIONS, useHasPermission } from 'contexts/PermissionContext';

const DefaultLanding: React.FC = () => {
  const hasAnalytics = useHasPermission(PERMISSIONS.ANALYTICS_PAGE_ACCESS);
  const hasDashboard = useHasPermission(PERMISSIONS.DASHBOARD_PAGE_ACCESS);
  return hasDashboard ?<PermissionGuard permission={PERMISSIONS.DASHBOARD_PAGE_ACCESS} showAccessDenied={true}>
      <NewDashboard />
    </PermissionGuard>: hasAnalytics ? (
    <PermissionGuard permission={PERMISSIONS.ANALYTICS_PAGE_ACCESS} showAccessDenied={true}>
      <DashboardAnalytics />
    </PermissionGuard>
  ) : (
    <TransactionOrder />
  );
};

const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/error/404')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/error/500')));
const MaintenanceUnderConstruction = Loadable(lazy(() => import('pages/maintenance/under-construction/under-construction')));
const MaintenanceComingSoon = Loadable(lazy(() => import('pages/maintenance/coming-soon/coming-soon')));
const LegalPublicPage = Loadable(lazy(() => import('pages/legal/LegalPublicPage')));

// ==============================|| MAIN ROUTES ||============================== //

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: '',
          element: <DefaultLanding />
        },
        {
          path: 'transactions',
          element: <TransactionOrder />
        },
        {
          path: 'transactions/unmapped',
          element: <UnmappedTransactionsData />
        },
        {
          path: 'clients',
          element: (
            <PermissionGuard permission={PERMISSIONS.CLIENT_PAGE_ACCESS} showAccessDenied={true}>
              <ClientsPage />
            </PermissionGuard>
          )
        },
        {
          path: 'clients/modify/:clientUid',
          element: (
            <PermissionGuard permission={PERMISSIONS.CLIENT_PAGE_ACCESS} showAccessDenied={true}>
              <ClientsPage />
            </PermissionGuard>
          )
        },
        {
          path: 'teams',
          element: <TeamsPage />
        },
        {
          path: 'teams/client/:clientUid',
          element: <TeamsPage />
        },
        {
          path: 'teams/modify/:teamUid',
          element: <TeamsPage />
        },
        {
          path: 'session/:sessionId',
          element: <VideoSessionPage />
        },
        {
          path: '/new-dashboard',
          element: (
            <PermissionGuard permission={PERMISSIONS.DASHBOARD_PAGE_ACCESS} showAccessDenied={true}>
              <NewDashboard />
            </PermissionGuard>
          )
        },
        {
          path: 'teams/flow/:teamUid',
          element: <QuickFlowApp />
        },
        {
          path: 'event/history/:transUid',
          element: <TransactionHistory />
        },
        {
          path: 'event/details/:historyUid',
          element: <TransactionDetails />
        },
        
        {
          path: 'transactions/details/:teamUid/:transUid?/:authKey?',
          element: <IndividualTransactionDetails />
        },
        {
          path: '/users',
          element: <Users/>
        },
        {
          path: '/resources',
          element: <Resources/>
        },
        {
          path: '/announcements',
          element: <Announcements />
        },
        {
          path: 'missed-calls',
          element: (
            <PermissionGuard permission={PERMISSIONS.CALLS_PAGE_ACCESS} showAccessDenied={true}>
              <MissedCalls />
            </PermissionGuard>
          )
        },
        {
          path: 'dashboard-analytics',
          element: (
            <PermissionGuard permission={PERMISSIONS.ANALYTICS_PAGE_ACCESS} showAccessDenied={true}>
              <DashboardAnalytics />
            </PermissionGuard>
          )
        },
        {
          path: 'ai-support',
          element: (
            <PermissionGuard permission={PERMISSIONS.AI_SUPPORT_PAGE_ACCESS} showAccessDenied={true}>
              <AiSupport />
            </PermissionGuard>
          )
        },
        {
          path: 'help',
          element: (
            <PermissionGuard permission={PERMISSIONS.HELP_PAGE_ACCESS} showAccessDenied={true}>
              <Help />
            </PermissionGuard>
          )
        }
      ]
    },
    {
      path: '/maintenance',
      element: <CommonLayout />,
      children: [
        {
          path: '404',
          element: <MaintenanceError />
        },
        {
          path: '500',
          element: <MaintenanceError500 />
        },
        {
          path: 'under-construction',
          element: <MaintenanceUnderConstruction />
        },
        {
          path: 'coming-soon',
          element: <MaintenanceComingSoon />
        }
      ]
    },
    // Public legal pages: no AuthGuard and no GuestGuard, so they load logged in or out.
    {
      path: '/',
      element: <CommonLayout />,
      children: [
        {
          path: 'privacy-policy',
          element: <LegalPublicPage slug="privacy-policy" />
        },
        {
          path: 'terms',
          element: <LegalPublicPage slug="terms" />
        }
      ]
    }
  ]
};

export default MainRoutes;
